import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AlertCircle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { buildInvestPagePath } from "lib/investmentHelper";
import { cn } from "lib/utils";

const VARIANT_CONFIG = {
  success: {
    icon: CheckCircle2,
    iconClass: "text-emerald-600 dark:text-emerald-500",
    ringClass: "bg-emerald-500/10",
  },
  cancel: {
    icon: XCircle,
    iconClass: "text-muted-foreground",
    ringClass: "bg-muted",
  },
  failed: {
    icon: AlertCircle,
    iconClass: "text-destructive",
    ringClass: "bg-destructive/10",
  },
  loading: {
    icon: Loader2,
    iconClass: "text-accent animate-spin",
    ringClass: "bg-accent/10",
  },
};

export function PaymentOutcomeLayout({
  variant,
  pageTitle,
  title,
  description,
  children,
  primaryAction,
  secondaryAction,
}) {
  const config = VARIANT_CONFIG[variant] ?? VARIANT_CONFIG.loading;
  const Icon = config.icon;

  return (
    <>
      <Helmet>
        <title>{pageTitle} | Fortify</title>
      </Helmet>
      <div className="container mx-auto max-w-lg px-4 py-16 sm:px-6 lg:px-12 xl:px-16">
        <Card>
          <CardContent className="space-y-6 pt-8 pb-8 text-center">
            <div
              className={cn(
                "mx-auto flex h-16 w-16 items-center justify-center rounded-full",
                config.ringClass
              )}
            >
              <Icon className={cn("h-8 w-8", config.iconClass)} aria-hidden />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
              {description ? (
                <p className="text-sm text-muted-foreground">{description}</p>
              ) : null}
            </div>
            {children}
            <div className="flex flex-col gap-3 pt-2">
              {primaryAction}
              {secondaryAction}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

PaymentOutcomeLayout.propTypes = {
  variant: PropTypes.oneOf(["success", "cancel", "failed", "loading"]).isRequired,
  pageTitle: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  children: PropTypes.node,
  primaryAction: PropTypes.node,
  secondaryAction: PropTypes.node,
};

function buildRetryPath(campaignId, amount) {
  if (!campaignId) return null;
  if (Number.isFinite(Number(amount))) {
    return buildInvestPagePath(campaignId, amount, 0, undefined);
  }
  return `/campaign/${campaignId}`;
}

export function PaymentOutcomeActions({
  campaignId,
  amount,
  showRetry = false,
}) {
  const investPath = buildRetryPath(campaignId, amount);

  return (
    <>
      <Button className="w-full" asChild>
        <Link to="/dashboard">View dashboard</Link>
      </Button>
      {showRetry && investPath ? (
        <Button variant="default" className="w-full" asChild>
          <Link to={investPath}>Try again</Link>
        </Button>
      ) : null}
      {campaignId ? (
        <Button variant="outline" className="w-full" asChild>
          <Link to={`/campaign/${campaignId}`}>Back to campaign</Link>
        </Button>
      ) : null}
    </>
  );
}

PaymentOutcomeActions.propTypes = {
  campaignId: PropTypes.string,
  amount: PropTypes.number,
  showRetry: PropTypes.bool,
};
